import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, COMPANY } from "../../data/site";

const DesktopItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  const base = ({ isActive }) => `relative px-2.5 xl:px-3 py-2 text-[14px] font-semibold transition-colors duration-300 ${isActive ? "text-moss" : "text-ink/75 hover:text-moss"}`;
  if (!item.children) return <NavLink to={item.path} className={base} data-testid={`nav-${item.path.slice(1)}`}>{item.label}</NavLink>;
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <NavLink to={item.path} className={base} data-testid={`nav-${item.path.slice(1)}`}>
        <span className="inline-flex items-center gap-1">{item.label}<ChevronDown className="w-3.5 h-3.5" /></span>
      </NavLink>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.2 }}
            className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-72">
            <div className="rounded-2xl bg-cream border border-moss/15 shadow-2xl shadow-forest/10 p-2">
              {item.children.map(z => (
                <Link key={z.slug} to={z.path} data-testid={`nav-sub-${z.slug}`} className="group flex items-center justify-between rounded-xl px-4 py-3 hover:bg-sand/70 transition-colors">
                  <span><span className="block text-sm font-bold text-forest">{z.name}</span><span className="block text-xs text-sage">{z.tag}</span></span>
                  <ArrowUpRight className="w-4 h-4 text-moss transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MobileMenu = ({ onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] bg-forest text-cream lg:hidden overflow-y-auto" data-lenis-prevent data-testid="mobile-menu">
    <div className="flex items-center justify-between px-5 h-20">
      <Logo light />
      <button onClick={onClose} aria-label="Fermer le menu" data-testid="mobile-menu-close" className="w-11 h-11 rounded-full border border-cream/30 grid place-items-center"><X className="w-5 h-5" /></button>
    </div>
    <nav className="px-5 pb-32 pt-4 space-y-1">
      {[{ label: "Accueil", path: "/" }, ...NAV, { label: "Contact", path: "/contact" }].map((item, i) => (
        <motion.div key={item.path} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * i }}>
          <Link to={item.path} onClick={onClose} className="block py-2 font-display text-3xl font-extrabold" data-testid={`mobile-nav-${item.path.slice(1) || "home"}`}>{item.label}</Link>
          {item.children && (
            <div className="grid grid-cols-2 gap-2 pb-3">
              {item.children.map(z => <Link key={z.slug} to={z.path} onClick={onClose} className="rounded-full border border-cream/20 px-3 py-2 text-sm text-fern">{z.name}</Link>)}
            </div>
          )}
        </motion.div>
      ))}
      <a href={COMPANY.phoneHref} className="btn btn-lime w-full mt-6" data-testid="mobile-menu-call"><Phone className="w-4 h-4" />{COMPANY.phone}</a>
    </nav>
  </motion.div>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      <header data-testid="site-header" className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,height] duration-500 ${scrolled ? "bg-cream/80 backdrop-blur-xl shadow-[0_1px_0_rgba(26,77,46,.12)]" : "bg-cream/60 backdrop-blur-md"}`}>
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 h-20 flex items-center justify-between gap-6">
          <Link to="/" aria-label="Moustique Bordeaux - accueil" data-testid="header-logo"><Logo /></Link>
          <nav className="hidden lg:flex items-center" aria-label="Navigation principale">
            {NAV.map(item => <DesktopItem key={item.path} item={item} />)}
          </nav>
          <div className="flex items-center gap-2">
            <a href={COMPANY.phoneHref} data-testid="header-call" className="hidden xl:inline-flex btn btn-ghost !py-2.5"><Phone className="w-4 h-4" />{COMPANY.phone}</a>
            <Link to="/contact" data-testid="header-quote" className="hidden sm:inline-flex btn btn-forest !py-2.5">Devis gratuit</Link>
            <button onClick={() => setOpen(true)} aria-label="Ouvrir le menu" data-testid="mobile-menu-open" className="lg:hidden w-11 h-11 rounded-full bg-moss text-cream grid place-items-center"><Menu className="w-5 h-5" /></button>
          </div>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
